import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import { listPakketten, listProductgroepen } from "@/features/pakketten/pakketten.api";
import { fmtMoney, fmtNumber } from "@/lib/format";
import type { Niveau, Pakket, Productgroep } from "@/lib/types";

// De prijslijst. Bewust een tabel en geen plan-kaarten: er komen 150–200 codes in,
// en een kaart per code is dan onleesbaar. Groeperen per productgroep, filteren op
// niveau en status, zoeken op productcode.
export const Route = createFileRoute("/_shell/pakketten")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search["q"] === "string" ? search["q"] : undefined,
    productgroep: typeof search["productgroep"] === "string" ? search["productgroep"] : undefined,
    niveau: typeof search["niveau"] === "string" ? search["niveau"] : undefined,
    status: typeof search["status"] === "string" ? search["status"] : undefined,
  }),
  component: PrijslijstPage,
});

const NIVEAUS: Niveau[] = ["standard", "advanced", "premium"];
// Niveaunamen zijn merknamen, niet UI-tekst: één set voor alle productgroepen.
const NIVEAU_LABELS: Record<Niveau, string> = {
  standard: "Standard",
  advanced: "Advanced",
  premium: "Premium",
};

function PrijslijstPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const groepen = useQuery({ queryKey: ["productgroepen"], queryFn: listProductgroepen });
  const query = useQuery({
    queryKey: ["pakketten", search],
    queryFn: () =>
      listPakketten({
        q: search.q,
        productgroep: search.productgroep,
        niveau: search.niveau,
        status: search.status,
        pageSize: "250",
      }),
    placeholderData: keepPreviousData,
  });

  const pakketten = query.data?.data ?? [];
  const totaal = query.data?.total ?? 0;

  // Groeperen in de weergave; de API sorteert al op groep → model → niveau.
  const perGroep = new Map<Productgroep, Pakket[]>();
  for (const p of pakketten) {
    const lijst = perGroep.get(p.productgroep) ?? [];
    lijst.push(p);
    perGroep.set(p.productgroep, lijst);
  }

  const setSearch = (patch: Record<string, string | undefined>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) });

  return (
    <div className="app-page" data-screen-label="Backend Prijslijst">
      <div className="page-header">
        <div>
          <div className="page-header__title">
            <h1>{t("pakketten.title")}</h1>
          </div>
          <p className="page-header__sub">{t("pakketten.subtitle")}</p>
        </div>
        <div className="page-header__actions">
          <button className="dtw-btn dtw-btn--accent">
            <Icon name="plus" size={14} /> {t("action.newPakket")}
          </button>
        </div>
      </div>

      {/* Grootboekdimensie: vijf groepen. Leeg = nog niet ingesteld, nooit een
          placeholdernummer — dat wordt overgenomen in Snelstart. */}
      <div className="groep-strip">
        {(groepen.data ?? []).map((g) => (
          <button
            key={g.productgroep}
            type="button"
            className={`groep-card${search.productgroep === g.productgroep ? " is-active" : ""}`}
            onClick={() =>
              setSearch({
                productgroep: search.productgroep === g.productgroep ? undefined : g.productgroep,
              })
            }
          >
            <span className="groep-card__name">{g.label}</span>
            <span className="groep-card__count">
              <b>{fmtNumber(g.aantalCodes)}</b> codes
            </span>
            <span className="groep-card__ledger">
              {t("pakketten.grootboek")}:{" "}
              {g.grootboekrekening ? (
                <span className="mono">{g.grootboekrekening}</span>
              ) : (
                <span className="t-tertiary">{t("pakketten.grootboekLeeg")}</span>
              )}
            </span>
          </button>
        ))}
      </div>

      <div className="panel">
        <div className="panel__head">
          <div className="views">
            <button
              type="button"
              className={`view-chip${!search.niveau ? " is-active" : ""}`}
              onClick={() => setSearch({ niveau: undefined })}
            >
              {t("pakketten.filter.alle")}
            </button>
            {NIVEAUS.map((n) => (
              <button
                key={n}
                type="button"
                className={`view-chip${search.niveau === n ? " is-active" : ""}`}
                onClick={() => setSearch({ niveau: n })}
              >
                {NIVEAU_LABELS[n]}
              </button>
            ))}
          </div>
          <span className="dtw-spacer" />
          <div className="dtw-input-group" style={{ width: "260px", height: "var(--control-height-sm)" }}>
            <span className="dtw-input-group__icon">
              <Icon name="search" size={13} />
            </span>
            <input
              placeholder={t("pakketten.search")}
              defaultValue={search.q ?? ""}
              onChange={(e) => setSearch({ q: e.target.value || undefined })}
            />
          </div>
          <span className="meta">{t("common.results", { count: totaal })}</span>
        </div>

        <div className="panel__body flush">
          {pakketten.length === 0 ? (
            <div className="empty">
              <div className="empty__icon">
                <Icon name="package" size={22} />
              </div>
              <h3>{t("pakketten.empty.title")}</h3>
              <p>{t("pakketten.empty.body")}</p>
            </div>
          ) : (
            <div className="dtw-table-wrap">
              <table className="dtw-table">
                <thead>
                  <tr>
                    <th>{t("pakketten.col.productcode")}</th>
                    <th>{t("pakketten.col.pakket")}</th>
                    <th>{t("pakketten.col.niveau")}</th>
                    <th className="num">{t("pakketten.col.maandtarief")}</th>
                    <th className="num">{t("pakketten.col.jaartarief")}</th>
                    <th className="num">{t("pakketten.col.inGebruik")}</th>
                    <th>{t("pakketten.col.status")}</th>
                  </tr>
                </thead>
                {[...perGroep.entries()].map(([groep, regels]) => (
                  <tbody key={groep}>
                    <tr className="dtw-table__group">
                      <td colSpan={7}>
                        {regels[0]?.productgroepLabel ?? groep}
                        <span className="meta">{t("common.results", { count: regels.length })}</span>
                      </td>
                    </tr>
                    {regels.map((p) => (
                      <tr key={p.id} className={p.status === "uitgefaseerd" ? "is-muted" : ""}>
                        <td className="mono">{p.productcode}</td>
                        <td>
                          <div className="dtw-table__cell-stack">
                            <span className="primary">{p.model}</span>
                            <span className="secondary t-tertiary">{p.omschrijving}</span>
                          </div>
                        </td>
                        <td>
                          <span className="dtw-chip dtw-chip--indigo">{p.niveauLabel}</span>
                        </td>
                        <td className="num mono">{fmtMoney(p.maandtariefCents)}</td>
                        <td className="num mono t-tertiary">{fmtMoney(p.jaartariefCents)}</td>
                        <td className="num mono">{fmtNumber(p.aantalInGebruik)}</td>
                        <td>
                          <span
                            className={`dtw-chip${
                              p.status === "actief"
                                ? " dtw-chip--eco"
                                : p.status === "uitgefaseerd"
                                  ? ""
                                  : " dtw-chip--brand"
                            }`}
                          >
                            {p.statusLabel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          )}
        </div>
        <div className="panel__foot">
          <span className="t-tertiary">{t("pakketten.jaarNote")}</span>
        </div>
      </div>
    </div>
  );
}
